import { LegacyRef, ReactNode, forwardRef } from 'react';
import {
	Flex,
	Text,
	FormControl,
	FormControlProps,
	FormErrorMessage,
	FormLabel,
	FormLabelProps,
	Input,
	InputProps,
} from 'src/components/ui';
import { InfoIcon } from '@chakra-ui/icons';

export interface CurrencyInputProps {
	label?: string;
	subLabel?: string;
	requirementsLabel?: string;
	id?: string;
	placeholder?: string;
	type?: 'text' | 'email' | 'password';
	error?: ReactNode;
	tooltip?: string;
	required?: boolean;
	requirementsLabelStyle?: React.CSSProperties;
	formControlProps?: FormControlProps;
	formLabelProps?: FormLabelProps;
	inputProps?: InputProps;
	direction?: 'row' | 'column';
}

const CurrencyInput = (
	{
		error,
		label,
		subLabel,
		requirementsLabel,
		placeholder,
		required,
		formControlProps,
		requirementsLabelStyle,
		formLabelProps,
		inputProps,
		direction = 'column',
	}: CurrencyInputProps,
	ref: LegacyRef<HTMLInputElement>,
) => {
	const hasError = !!error;

	const renderLabelIndicator = () => {
		if (!required) return null;

		if (error) return <InfoIcon color="#e53e3e" />;

		return <Text color="#e53e3e">*</Text>;
	};

	const errorFocusProps = error
		? {
				boxShadow: '0 0 0 1px #E53E3E',
				borderColor: '#E53E3E',
			}
		: {
				boxShadow: '0 0 0 1px #3182ce',
				borderColor: '#3182ce',
			};

	return (
		<FormControl
			w="full"
			isInvalid={hasError}
			{...formControlProps}
			display="flex"
			alignItems="start"
			flexDirection={
				direction === 'row'
					? { base: 'column', md: 'row' }
					: 'column'
			}
			gap={direction === 'row' ? { base: 2, md: 20 } : 0}
		>
			{label && (
				<Flex flex={{ base: 1, md: 'initial' }}>
					<FormLabel mb="2" {...formLabelProps}>
						<Flex gap={1} alignItems="left" fontSize={{ base: 14, md: 16 }}>
							{label}
							{subLabel && (
								<Text color="#718096" fontSize="smaller">
									{subLabel}
								</Text>
							)}
							{renderLabelIndicator()}
						</Flex>
						{requirementsLabel && (
							<Text
								fontSize={{ base: '11px', md: '12px' }}
								color="#959595"
								style={requirementsLabelStyle}
							>
								{requirementsLabel}
							</Text>
						)}
					</FormLabel>
				</Flex>
			)}
			<Input
				flex={2}
				size={{ base: 'sm', md: 'md' }}
				maxW={{ base: 'full', md: '150px' }}
				marginTop={{ base: 0, md: '9px' }}
				fontSize="14px"
				_placeholder={{ color: '#A0AEC0' }}
				placeholder={placeholder}
				{...inputProps}
				ref={ref}
				_focusVisible={errorFocusProps}
			/>
			{error && <FormErrorMessage>{error}</FormErrorMessage>}
		</FormControl>
	);
};

export default forwardRef(CurrencyInput);
